import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {BoletoCheckoutResponse, CheckoutConfigResponse, PixQrCodeResponse} from "../models/CheckoutConfigResponse";
import {MAT_DIALOG_DATA, MatDialogModule} from "@angular/material/dialog";
import {InvoiceService} from "../invoice/invoice.service";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import {InvoiceNew} from "../models/InvoiceNew";
import {firstValueFrom} from 'rxjs';

export interface PaymentCheckoutModalData {
  mode: 'pix' | 'boleto';
  checkoutConfigResponse?: CheckoutConfigResponse;
  invoice?: InvoiceNew;
}

@Component({
  selector: 'app-payment-checkout-modal',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatButtonModule, MatDialogModule],
  templateUrl: './payment-checkout-modal.component.html',
  styleUrls: ['./payment-checkout-modal.component.css']
})
export class PaymentCheckoutModalComponent implements OnInit, OnDestroy{

  public constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: PaymentCheckoutModalData,
    public invoiceService: InvoiceService,
    private sanitizer: DomSanitizer,
  ) {
  }
  public pdfBoletoSanitizado: SafeResourceUrl | null = null;
  public pdfBoleto: string = "";

  async ngOnInit(): Promise<any> {
    if (this.data.mode === 'boleto') {
      await this.getBoletoRender();
    }
  }

  public get checkoutConfigResponse(): CheckoutConfigResponse | undefined {
    return this.data.checkoutConfigResponse;
  }

  public get pix(): PixQrCodeResponse | null | undefined {
    return this.data.checkoutConfigResponse?.PixQrCodeResponse;
  }

  public get boleto(): BoletoCheckoutResponse | null | undefined {
    return this.data.checkoutConfigResponse?.BoletoCheckoutResponse;
  }

  public get boletoCode(): string {
    return this.boleto?.BarCode || this.data.invoice?.CODIGO_BARRAS_BOLETO || "";
  }

  public get boletoDueDate(): Date | undefined {
    return this.boleto?.DueDate || this.data.invoice?.DATA_VENCIMENTO;
  }

  public get boletoValue(): number | undefined {
    return this.boleto?.Value || this.data.invoice?.VALOR_TOTAL;
  }

  public async copiarCodigoBarrasBoleto(): Promise<void> {
    await this.copyToClipboard(this.boletoCode, "Código do boleto copiado");
  }

  public async copiarQrCodePix(): Promise<void> {
    await this.copyToClipboard(this.pix?.COPIA_COLA ?? "", "Código Pix copiado");
  }

  public async getBoletoRender(): Promise<void>{
    const boletoId = this.boleto?.Id || this.data.invoice?.CODIGO_BOLETO;
    if (!boletoId) {
      return;
    }

    try {
      // O iframe não passa pelo HttpClient e, portanto, não recebe o token.
      // O documento HTML do boleto precisa ser baixado autenticado antes de
      // ser disponibilizado ao iframe por meio de uma URL de objeto local.
      const boletoHtml = await firstValueFrom(this.invoiceService.getBankInvoice(boletoId, 1));
      this.pdfBoleto = URL.createObjectURL(boletoHtml);
      this.pdfBoletoSanitizado = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBoleto);
    } catch (error) {
      console.error(error);
      this.pdfBoleto = "";
      this.pdfBoletoSanitizado = null;
      this.invoiceService.toasterError('Erro ao gerar boleto');
    }
  }

  public ngOnDestroy(): void {
    if (this.pdfBoleto) {
      URL.revokeObjectURL(this.pdfBoleto);
    }
  }

  public async imprimirBoleto(): Promise<void>{
    if (!this.pdfBoleto) {
      this.invoiceService.toasterError('Boleto indisponível para impressão.');
      return;
    }

    window.open(this.pdfBoleto, '_blank');
  }

  private async copyToClipboard(value: string, successMessage: string): Promise<void> {
    if (!value) {
      this.invoiceService.toasterError('Código indisponível para copiar.');
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        this.copyToClipboardFallback(value);
      }
      this.invoiceService.toasterSuccess(successMessage);
    } catch {
      this.copyToClipboardFallback(value);
      this.invoiceService.toasterSuccess(successMessage);
    }
  }

  private copyToClipboardFallback(value: string): void {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = value;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }
}
